// src/components/features/integrations/ActiveCampaignSetupForm.jsx
import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalFooter,
} from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { IntegrationService } from '@/services/integration.service';

export function ActiveCampaignSetupForm({
  pixelId,
  onComplete,
  open,
  onOpenChange,
}) {
  const [step, setStep] = useState('initial');
  const [formData, setFormData] = useState({ apiUrl: '', apiKey: '' });
  const [error, setError] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const [integrationData, setIntegrationData] = useState(null);

  useEffect(() => {
    console.log('[ActiveCampaignSetupForm] Mount with initial state:', {
      step,
      open,
      pixelId,
    });
    return () => {
      console.log('[ActiveCampaignSetupForm] Unmounting');
    };
  }, []);

  useEffect(() => {
    console.log('[ActiveCampaignSetupForm] Step changed:', step);
  }, [step]);

  useEffect(() => {
    console.log('[ActiveCampaignSetupForm] Open state changed:', open);
  }, [open]);

  const handleOpenChange = (isOpen) => {
    console.log('[ActiveCampaignSetupForm] handleOpenChange called:', {
      isOpen,
      currentStep: step,
      isClosing,
    });

    if (!isOpen && !isClosing) {
      if (step === 'completed') {
        console.log('[ActiveCampaignSetupForm] Completing integration...');
        onComplete?.();
      } else {
        console.log('[ActiveCampaignSetupForm] Resetting form state...');
        setStep('initial');
        setFormData({ apiUrl: '', apiKey: '' });
        setError(null);
        setIntegrationData(null);
      }
      onOpenChange(false);
    }
  };

  const handleInputChange = (e) => {
    console.log('[ActiveCampaignSetupForm] Input changed:', e.target.name);
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
    setError(null);
  };

  const handleValidate = async () => {
    console.log('[ActiveCampaignSetupForm] Starting validation...');
    setStep('validating');
    setError(null);

    try {
      console.log('[ActiveCampaignSetupForm] Calling validate API...');
      await IntegrationService.validateActiveCampaign(pixelId, {
        apiUrl: formData.apiUrl,
        apiKey: formData.apiKey,
      });

      console.log(
        '[ActiveCampaignSetupForm] Validation successful, moving to integration...',
      );
      setStep('integrating');
      handleIntegrate();
    } catch (err) {
      console.error('[ActiveCampaignSetupForm] Validation error:', err);
      setError(err.response?.data?.message || 'Failed to validate credentials');
      setStep('initial');
    }
  };

  const handleIntegrate = async () => {
    console.log('[ActiveCampaignSetupForm] Starting integration...');
    try {
      const response = await IntegrationService.setupActiveCampaign(pixelId, {
        apiUrl: formData.apiUrl,
        apiKey: formData.apiKey,
      });

      console.log(
        '[ActiveCampaignSetupForm] Integration successful, moving to completed state...',
      );
      setIntegrationData(response);
      setStep('completed');
    } catch (err) {
      console.error('[ActiveCampaignSetupForm] Integration error:', err);
      setError(err.response?.data?.message || 'Failed to set up integration');
      setStep('initial');
    }
  };

  const handleClose = () => {
    console.log('[ActiveCampaignSetupForm] handleClose called:', {
      currentStep: step,
      isClosing,
    });

    setIsClosing(true);

    if (step === 'completed') {
      console.log(
        '[ActiveCampaignSetupForm] Completing integration before close...',
      );
      onComplete?.();
    }

    setTimeout(() => {
      console.log('[ActiveCampaignSetupForm] Closing modal...');
      onOpenChange(false);
      setIsClosing(false);
    }, 0);
  };

  const renderContent = () => {
    console.log('[ActiveCampaignSetupForm] Rendering content for step:', step);

    switch (step) {
      case 'initial':
        return (
          <>
            <ModalHeader>
              <ModalTitle>Connect Active Campaign</ModalTitle>
              <ModalDescription>
                Enter your Active Campaign API credentials to connect your
                account.
              </ModalDescription>
            </ModalHeader>

            <div className="sl-form space-y-4 py-4">
              <div className="sl-form-group">
                <label className="sl-form-label block text-sm font-medium text-gray-700">
                  API Base URL
                </label>
                <input
                  type="text"
                  name="apiUrl"
                  value={formData.apiUrl}
                  onChange={handleInputChange}
                  placeholder="https://your-account.api-us1.com"
                  className="sl-form-input mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>

              <div className="sl-form-group">
                <label className="sl-form-label block text-sm font-medium text-gray-700">
                  API Key
                </label>
                <input
                  type="password"
                  name="apiKey"
                  value={formData.apiKey}
                  onChange={handleInputChange}
                  className="sl-form-input mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>

              {error && (
                <div className="sl-error flex items-center gap-2 rounded-md bg-red-50 p-3 text-sm text-red-700">
                  <AlertCircle className="h-4 w-4" />
                  {error}
                </div>
              )}
            </div>

            <ModalFooter>
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleValidate}
                disabled={!formData.apiUrl || !formData.apiKey}>
                Continue
              </Button>
            </ModalFooter>
          </>
        );

      case 'validating':
      case 'integrating':
        return (
          <>
            <ModalHeader>
              <ModalTitle>Setting Up Integration</ModalTitle>
              <ModalDescription>
                Please wait while we set up your Active Campaign integration.
              </ModalDescription>
            </ModalHeader>

            <div className="sl-progress space-y-4 py-6">
              <div className="sl-step flex items-center gap-3">
                <div className="sl-step-icon">
                  {step === 'validating' ? (
                    <Loader2 className="h-5 w-5 animate-spin text-primary-500" />
                  ) : (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  )}
                </div>
                <div className="sl-step-text text-sm text-gray-600">
                  Validating API credentials
                </div>
              </div>

              <div className="sl-step flex items-center gap-3">
                <div className="sl-step-icon">
                  {step === 'integrating' ? (
                    <Loader2 className="h-5 w-5 animate-spin text-primary-500" />
                  ) : (
                    <div className="h-5 w-5 rounded-full border-2 border-gray-200" />
                  )}
                </div>
                <div className="sl-step-text text-sm text-gray-600">
                  Setting up integration
                </div>
              </div>
            </div>
          </>
        );

      case 'completed':
        return (
          <>
            <ModalHeader>
              <ModalTitle>Integration Complete!</ModalTitle>
              <ModalDescription>
                Your Active Campaign integration has been set up successfully.
              </ModalDescription>
            </ModalHeader>

            <div className="sl-success-message py-6">
              <div className="sl-icon-container mb-4 flex justify-center">
                <div className="sl-success-icon rounded-full bg-green-100 p-3">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
              </div>

              <div className="sl-integration-details space-y-4">
                <p className="sl-message text-center text-sm text-gray-600">
                  Source data from your pixel will now be automatically synced
                  with your Active Campaign contacts.
                </p>

                <div className="sl-source-field-info mt-4 rounded-lg bg-gray-50 p-4">
                  <h4 className="sl-info-title mb-2 text-sm font-medium text-gray-700">
                    Source Data Field
                  </h4>
                  <p className="sl-info-description text-xs text-gray-500">
                    Your contacts' marketing source data will be stored in the
                    following custom field:
                  </p>
                  <code className="sl-field-id mt-2 block rounded bg-white px-3 py-2 text-sm font-mono text-purple-600 border border-gray-200">
                    Field ID: {integrationData?.sourceFieldId || 'Unknown'}{' '}
                  </code>
                </div>
              </div>
            </div>

            <ModalFooter>
              <Button variant="primary" onClick={handleClose}>
                Done
              </Button>
            </ModalFooter>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Modal open={open} onOpenChange={handleOpenChange}>
      <ModalContent className="sm:max-w-lg">{renderContent()}</ModalContent>
    </Modal>
  );
}
